// Tidy clipboard HTML from Word, Google Docs, Notion, and friends before
// TipTap parses it. Everything here works on a detached document so nothing
// touches the page. Returns plain, attribute-free HTML that maps onto the
// editor's schema: p, headings, strong, em, u, s, a, ul, ol, li, blockquote,
// hr, br, img.

const KEEP_ATTRS = { a: ['href'], img: ['src', 'alt'] }

const styleHas = (el, prop, test) => {
  const value = (el.getAttribute('style') || '').toLowerCase().replace(/\s+/g, '')
  const match = value.match(new RegExp(`${prop}:([^;]+)`))
  return match ? test(match[1]) : false
}

const unwrap = (el) => {
  const parent = el.parentNode
  if (!parent) { return }
  while (el.firstChild) { parent.insertBefore(el.firstChild, el) }
  parent.removeChild(el)
}

const wrapIn = (doc, el, tag) => {
  const wrapper = doc.createElement(tag)
  while (el.firstChild) { wrapper.appendChild(el.firstChild) }
  el.appendChild(wrapper)
}

const isBlank = (el) => el.textContent.replace(/\u00a0/g, ' ').trim() === '' && !el.querySelector('img, hr')

// Word marks list paragraphs with mso-list styles or MsoListParagraph classes
// and prints the bullet or number as literal text inside a hidden span.
const isWordListParagraph = (p) =>
  /mso-list/i.test(p.getAttribute('style') || '') || /MsoListParagraph/i.test(p.getAttribute('class') || '')

const wordListsToRealLists = (doc) => {
  const paragraphs = Array.from(doc.body.querySelectorAll('p')).filter(isWordListParagraph)
  let list = null
  for (const p of paragraphs) {
    p.querySelectorAll('[style*="mso-list:Ignore"], [style*="mso-list: Ignore"]').forEach((n) => n.remove())
    const text = p.textContent.replace(/\u00a0/g, ' ').trim()
    const ordered = /^\d+[.)]/.test(text) || /^[a-z][.)]/i.test(text)
    // Strip a leftover marker like "1." or "·" if the hidden span was not present.
    p.innerHTML = p.innerHTML.replace(/^(\s|&nbsp;)*(?:[·•▪o§-]|\d+[.)]|[a-z][.)])(\s|&nbsp;)+/i, '')
    const tag = ordered ? 'ol' : 'ul'
    const prevIsThisList = list && list.tagName.toLowerCase() === tag && p.previousElementSibling === list
    if (!prevIsThisList) {
      list = doc.createElement(tag)
      p.parentNode.insertBefore(list, p)
    }
    const li = doc.createElement('li')
    while (p.firstChild) { li.appendChild(p.firstChild) }
    list.appendChild(li)
    p.remove()
  }
}

export default function cleanPastedHtml (html) {
  if (!html || typeof window === 'undefined' || !window.DOMParser) { return html }
  const doc = new window.DOMParser().parseFromString(html, 'text/html')
  const body = doc.body

  // Office and Google noise that carries no content.
  body.querySelectorAll('meta, link, style, script, title, xml, colgroup, col').forEach((n) => n.remove())
  Array.from(body.querySelectorAll('*')).forEach((el) => {
    if (el.tagName.includes(':')) { unwrap(el) } // <o:p>, <w:sdt>, ...
  })
  const walker = doc.createTreeWalker(body, window.NodeFilter.SHOW_COMMENT)
  const comments = []
  while (walker.nextNode()) { comments.push(walker.currentNode) }
  comments.forEach((c) => c.remove())

  // Google Docs wraps the whole paste in <b style="font-weight:normal">.
  body.querySelectorAll('b[style*="font-weight:normal"], b[style*="font-weight: normal"], b[id^="docs-internal-guid"]').forEach(unwrap)

  wordListsToRealLists(doc)

  // Inline styles carry the real formatting in Docs and Word. Turn them into
  // tags the editor knows, then drop the span.
  Array.from(body.querySelectorAll('span, font, b, i')).forEach((el) => {
    if (!el.parentNode) { return }
    if (el.tagName === 'B') { const s = doc.createElement('strong'); s.innerHTML = el.innerHTML; el.replaceWith(s); return }
    if (el.tagName === 'I') { const e = doc.createElement('em'); e.innerHTML = el.innerHTML; el.replaceWith(e); return }
    if (styleHas(el, 'font-weight', (v) => v === 'bold' || parseInt(v, 10) >= 600)) { wrapIn(doc, el, 'strong') }
    if (styleHas(el, 'font-style', (v) => v === 'italic')) { wrapIn(doc, el, 'em') }
    if (styleHas(el, 'text-decoration', (v) => v.includes('underline')) || styleHas(el, 'text-decoration-line', (v) => v.includes('underline'))) { wrapIn(doc, el, 'u') }
    if (styleHas(el, 'text-decoration', (v) => v.includes('line-through')) || styleHas(el, 'text-decoration-line', (v) => v.includes('line-through'))) { wrapIn(doc, el, 's') }
    unwrap(el)
  })

  // Headings pasted as styled paragraphs stay paragraphs; the schema will keep
  // real h1-h6. Divs become paragraphs so line structure survives.
  body.querySelectorAll('div').forEach((div) => {
    if (div.querySelector('p, div, ul, ol, h1, h2, h3, h4, h5, h6, table')) { unwrap(div); return }
    const p = doc.createElement('p'); p.innerHTML = div.innerHTML; div.replaceWith(p)
  })

  // Strip every attribute we do not explicitly keep.
  Array.from(body.querySelectorAll('*')).forEach((el) => {
    const keep = KEEP_ATTRS[el.tagName.toLowerCase()] || []
    Array.from(el.attributes).forEach((attr) => { if (!keep.includes(attr.name)) { el.removeAttribute(attr.name) } })
  })

  // Images that point nowhere useful (data URIs, local Word paths) come out.
  body.querySelectorAll('img').forEach((img) => {
    if (!/^https?:\/\//i.test(img.getAttribute('src') || '')) { img.remove() }
  })

  // Empty paragraphs and stray breaks between blocks.
  body.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6').forEach((el) => { if (isBlank(el)) { el.remove() } })
  body.querySelectorAll('ul:empty, ol:empty').forEach((el) => el.remove())

  return body.innerHTML.trim()
}
